<script setup lang="ts">
import { computed } from 'vue';
import { t } from './i18n';
import {
  isShapeId,
  normalizeShapeParams,
  renderShape,
  type ShapeDesignItem,
  type ShapeParams,
} from './shapes';

const props = defineProps<{
  item: ShapeDesignItem | null;
  update: (patch: Record<string, unknown>) => void;
}>();

const isShape = computed(() => props.item?.type === 'svg' && isShapeId(props.item.shapeId));
const params = computed(() => normalizeShapeParams(props.item?.shapeParams ?? {}));

function patch(next: Partial<ShapeParams>) {
  const item = props.item;
  if (!item || !isShapeId(item.shapeId)) return;
  const shapeParams = { ...(item.shapeParams ?? {}), ...next };
  const rendered = renderShape(item.shapeId, shapeParams, item.svgColors ?? {}, item.width, item.height);
  props.update({
    shapeParams,
    shapeSourceSvg: rendered.source,
    svg: rendered.svg,
    src: rendered.src,
  });
}

function setNumber(key: 'strokeWidth' | 'cornerRadius', event: Event) {
  patch({ [key]: Number((event.target as HTMLInputElement).value) });
}
</script>

<template>
  <section v-if="isShape" class="vptw:flex vptw:flex-col vptw:gap-4 vptw:rounded-xl vptw:border vptw:border-(--vs-line) vptw:bg-(--vs-surface) vptw:p-3">
    <div>
      <h3 class="vptw:m-0 vptw:text-sm vptw:font-semibold vptw:text-(--vs-ink)">{{ t('Shape') }}</h3>
      <p class="vptw:mt-1 vptw:mb-0 vptw:text-xs vptw:text-(--vs-ink-2)">{{ t('Vector layer') }}</p>
    </div>

    <label class="vptw:block">
      <span class="vptw:mb-1.5 vptw:block vptw:text-xs vptw:font-medium vptw:text-(--vs-ink-2)">{{ t('Stroke width') }}</span>
      <input
        type="range"
        min="0"
        max="24"
        step="1"
        :value="params.strokeWidth"
        class="vptw:w-full"
        @input="setNumber('strokeWidth', $event)"
      />
      <span class="vptw:text-[10px] vptw:text-(--vs-ink-3)">{{ params.strokeWidth }}px</span>
    </label>

    <label class="vptw:block">
      <span class="vptw:mb-1.5 vptw:block vptw:text-xs vptw:font-medium vptw:text-(--vs-ink-2)">{{ t('Corner radius') }}</span>
      <input
        type="range"
        min="0"
        max="48"
        step="1"
        :value="params.cornerRadius"
        :disabled="item?.shapeId !== 'rounded-rect'"
        class="vptw:w-full disabled:vptw:opacity-40"
        @input="setNumber('cornerRadius', $event)"
      />
      <span class="vptw:text-[10px] vptw:text-(--vs-ink-3)">{{ params.cornerRadius }}px</span>
    </label>
  </section>
</template>
